import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Datepicker from 'components/Datepicker'
import RightSideModal from 'components/RightSideModal/RightSideModal'
import { SelectFilter } from 'components/filters'
import calendarIMG from 'img/icons/misc/calendar.svg'
import {
	getInitialCreateReportFormState,
	REPORT_CALENDAR_CREATE_REPORT_BUSINESS_OPTIONS,
	REPORT_CALENDAR_CREATE_REPORT_DRAWER,
	REPORT_CALENDAR_CREATE_REPORT_TYPE_OPTIONS,
} from './reportCalendarCreateReportConfig'
import '../styles/report-calendar-create-report-drawer.sass'

const toIsoDate = (date) => {
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
		return null
	}

	const year = date.getFullYear()
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')

	return `${year}-${month}-${day}`
}

const CreateReportFormField = ({ label, htmlFor, children }) => (
	<div className='reporting-calendar-create-report-drawer__field'>
		<label className='reporting-calendar-create-report-drawer__label' htmlFor={htmlFor}>
			{label}
		</label>
		<div className='reporting-calendar-create-report-drawer__control'>
			{children}
		</div>
	</div>
)

const ReportCalendarCreateReportDrawer = ({
	open,
	onClose,
	defaultDate = null,
	onCreate,
}) => {
	const [form, setForm] = useState(() => getInitialCreateReportFormState(defaultDate))

	const isFormValid = useMemo(
		() => Boolean(form.business && form.date && form.reportType),
		[form.business, form.date, form.reportType],
	)

	const resetForm = useCallback(() => {
		setForm(getInitialCreateReportFormState(defaultDate))
	}, [defaultDate])

	useEffect(() => {
		if (!open) {
			return
		}

		resetForm()
	}, [open, resetForm])

	const handleClose = useCallback(() => {
		resetForm()
		onClose?.()
	}, [onClose, resetForm])

	const handleCreate = useCallback(() => {
		if (!isFormValid) {
			return
		}

		onCreate?.(form)
		handleClose()
	}, [form, handleClose, isFormValid, onCreate])

	const datePrefix = (
		<span className='reporting-calendar-create-report-drawer__date-prefix'>
			<img src={calendarIMG} alt='' width={20} height={20} draggable={false} />
		</span>
	)

	const footer = (
		<div className='reporting-calendar-create-report-drawer__footer'>
			<button
				type='button'
				className='reporting-calendar-create-report-drawer__submit-btn'
				onClick={handleCreate}
				disabled={!isFormValid}
			>
				{REPORT_CALENDAR_CREATE_REPORT_DRAWER.submitLabel}
			</button>
		</div>
	)

	return (
		<RightSideModal
			open={open}
			onClose={handleClose}
			title={REPORT_CALENDAR_CREATE_REPORT_DRAWER.title}
			width={REPORT_CALENDAR_CREATE_REPORT_DRAWER.width}
			className='RightSideModal--reportCalendarCreateReport'
			footer={footer}
			content={(
				<div className='reporting-calendar-create-report-drawer'>
					<div className='reporting-calendar-create-report-drawer__fields'>
						<CreateReportFormField
							label='Select business'
							htmlFor='report-calendar-create-business'
						>
							<SelectFilter
								id='report-calendar-create-business'
								width='100%'
								height={44}
								allowClear
								clearValue=''
								placeholder='Select'
								options={REPORT_CALENDAR_CREATE_REPORT_BUSINESS_OPTIONS}
								value={form.business}
								onChange={(event) => {
									setForm((current) => ({
										...current,
										business: event.target.value,
									}))
								}}
								ariaLabel='Select business'
							/>
						</CreateReportFormField>

						<CreateReportFormField label='Select date'>
							<Datepicker
								labelDisabled
								placeholder='Select date'
								isClearable
								clearOnlyWhenHasValue
								allowRange={false}
								value={form.date ? toIsoDate(form.date) : null}
								onChange={(date) => {
									setForm((current) => ({
										...current,
										date: date instanceof Date ? date : null,
									}))
								}}
								prefix={datePrefix}
							/>
						</CreateReportFormField>

						<CreateReportFormField
							label='Select report type'
							htmlFor='report-calendar-create-report-type'
						>
							<SelectFilter
								id='report-calendar-create-report-type'
								width='100%'
								height={44}
								allowClear
								clearValue=''
								placeholder='Select type'
								options={REPORT_CALENDAR_CREATE_REPORT_TYPE_OPTIONS}
								value={form.reportType}
								onChange={(event) => {
									setForm((current) => ({
										...current,
										reportType: event.target.value,
									}))
								}}
								ariaLabel='Select report type'
							/>
						</CreateReportFormField>
					</div>
				</div>
			)}
		/>
	)
}

export default ReportCalendarCreateReportDrawer
